import { useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Col, Row, Form } from 'antd';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { clearLogin, doSetLastMfaCodeSentAt, login, setAuthentication } from 'src/actions';
import { ErrorName } from 'src/constants';
import {
  AuthenticatedUserLoginResponseDto,
  ErrorResponseDto,
  LoginResponseDto,
  MfaRequiredLoginResponseDto,
} from 'src/dtos';
import { MInput, MInputPassword, MButton, CardLayout, MAlert, RequestErrorMessage } from 'src/lib';
import { BaseStyle, Spacing } from 'src/styles';
import {
  renderTooManyFailedLoginAttemptsErrorMessage,
  renderTooManyLoginAttemptsErrorMessage,
  renderTradingBlockPasswordExpirationErrorMessage,
  renderTradingBlockSamePasswordErrorMessage,
} from 'src/utils';
import * as Yup from 'yup';

import * as Styles from '../BaseLayout.styles';

export const loginValidationSchema = Yup.object().shape({
  email: Yup.string().required('Email is required').email('Email is invalid'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password should be 8 characters at minimum')
    .max(24, 'Password should be 24 characters at most'),
});

interface LoginFormValues {
  email: string;
  password: string;
}

export const Login = () => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();

  const loginError: ErrorResponseDto | undefined = useSelector((state: any) => state.user.login.error);

  const isLoginLoading: boolean = useSelector((state: any) => state.user.login.isLoading);
  const loginSucceded = useSelector((state: any) => Boolean(state.user.login.__succeeded));
  const loginData: LoginResponseDto | undefined = useSelector((state: any) => state.user.login.data);
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);

  const isMfaRequired = (loginData?: LoginResponseDto): loginData is MfaRequiredLoginResponseDto => {
    if (!loginData) return false;

    return (loginData as MfaRequiredLoginResponseDto).session !== undefined;
  };

  const isLoggedIn = (loginData?: LoginResponseDto): loginData is AuthenticatedUserLoginResponseDto => {
    if (!loginData) return false;

    return (loginData as AuthenticatedUserLoginResponseDto).user !== undefined;
  };

  const onResetPasswordRequest = () => {
    navigateTo('/forgot-password');
  };

  const renderLoginErrorMessage = () => {
    if (loginError?.error === ErrorName.TradingBlockSamePasswordError) {
      return renderTradingBlockSamePasswordErrorMessage({ onClick: onResetPasswordRequest });
    }

    if (loginError?.error === ErrorName.TradingBlockPasswordExpirationError) {
      return renderTradingBlockPasswordExpirationErrorMessage({ onClick: onResetPasswordRequest });
    }

    if (loginError?.name === ErrorName.TooManyFailedLoginAttemptsError) {
      return renderTooManyFailedLoginAttemptsErrorMessage();
    }

    if (loginError?.name === ErrorName.TooManyLoginAttemptsError) {
      return renderTooManyLoginAttemptsErrorMessage();
    }

    return <RequestErrorMessage name={loginError?.name} message={loginError?.message} />;
  };

  useEffect(() => {
    if (loginSucceded && isMfaRequired(loginData)) {
      dispatch(setAuthentication({ ...loginData, ...credentials }));
      dispatch(doSetLastMfaCodeSentAt(new Date()));

      return navigateTo('/login-mfa');
    }

    if (loginSucceded && isLoggedIn(loginData)) {
      window.gtag('event', 'login', {
        login_method: 'sfa',
        user_properties: {
          user_id: loginData.user.id,
          user_email: credentials?.email,
          user_first_name: loginData.user.firstName,
          user_last_name: loginData.user.lastName,
        },
      });
      const { accessToken, idToken, tbToken } = loginData;
      const authToken = `Bearer ${accessToken}; idToken ${idToken}; tbToken ${tbToken}`;
      dispatch(setAuthentication({ authToken, tbToken }));
      dispatch(doSetLastMfaCodeSentAt(null));

      return navigateTo('/');
    }
  }, [loginSucceded, loginData]);

  useEffect(() => {
    return () => {
      dispatch(clearLogin());
    };
  }, []);

  return (
    <CardLayout
      title='Welcome!'
      subtitle='In order to Invest in an Offer, you will first need to “Sign up” or “Log in” to My IPO'>
      <Formik<LoginFormValues>
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={loginValidationSchema}
        onSubmit={values => {
          setCredentials(values);
          dispatch(login(values));
        }}>
        {form => {
          return (
            <Form autoComplete='off' style={{ width: '100%' }}>
              <Row className={Spacing.my16}>
                <Col span={24} className={Spacing.my16}>
                  <MInput
                    placeholder='Email'
                    value={form.values.email}
                    onChange={value => form.setFieldValue('email', value)}
                    error={form.errors.email}
                    name='login-email-name-attribute'
                    autoComplete='off'
                    testId={'email'}
                  />
                </Col>
                <Col span={24} className={Spacing.my16}>
                  <MInputPassword
                    placeholder='Password'
                    value={form.values.password}
                    onChange={value => form.setFieldValue('password', value)}
                    error={form.errors.password}
                    name='login-password-name-attribute'
                    autoComplete='off'
                    testId={'password'}
                  />
                </Col>
              </Row>
              <Col span={24} className={Spacing.my16}>
                <Link className={Styles.link} to='/forgot-password' data-testid='forgot-password-link'>
                  Forgot your password?
                </Link>
              </Col>
              {loginError && (
                <Col span={24} className={Spacing.my16}>
                  <MAlert
                    type='error'
                    description={renderLoginErrorMessage()}
                    icon={<ExclamationCircleOutlined />}
                    showIcon
                    closable={false}
                    testId={'login-error-message'}
                  />
                </Col>
              )}
              <Col span={24} className={cx(Spacing.my16, BaseStyle.level)}>
                <MButton
                  type='secondary'
                  disabled={isLoginLoading || !form.isValid}
                  onClick={() => form.submitForm()}
                  loading={isLoginLoading}
                  testId={'login-button'}>
                  Log in
                </MButton>
              </Col>
              <Col span={24} className={Styles.alreadyHaveAccountLabel}>
                <span className={Styles.space} data-testid='do-not-have-acc-testId'>
                  Don&apos;t have an account?
                </span>
                <Link to='/register' className={Styles.linkButton} data-testid='signup-link'>
                  Sign up now.
                </Link>
              </Col>
            </Form>
          );
        }}
      </Formik>
    </CardLayout>
  );
};
